import { useCallback } from "react";
import { QuoteResponse } from "@forgepoint/shared";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { EntitySelector } from "@/components/EntitySelector";
import { formatConvertedAmount } from "@/lib/quoteCurrency";

interface QuoteSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (quoteId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
  excludeQuoteId?: string;
  unlinkedOnly?: boolean;
}

/**
 * Quote selector component that provides a searchable dropdown for selecting quotes.
 * Uses the generic EntitySelector with quote-specific configuration.
 * With unlinkedOnly, only quotes not linked to an opportunity are fetched, so
 * search and pagination run server-side over the eligible set.
 */
export function QuoteSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select a quote...",
  className,
  disabled = false,
  variant = "default",
  excludeQuoteId,
  unlinkedOnly,
}: QuoteSelectorProps) {
  const { quotes, listLoading, fetchQuotes } = useRecordQuotesStore();

  const fetchEntities = useCallback(
    async (search?: string) => {
      return fetchQuotes({ search, limit: 50, unlinkedOnly });
    },
    [fetchQuotes, unlinkedOnly],
  );

  const getDisplayName = useCallback((quote: QuoteResponse) => {
    let display = quote.quoteDisplayNumber;
    if (quote.revision > 0) {
      display += ` Rev ${quote.revision}`;
    }
    if (quote.name) {
      display += ` - ${quote.name}`;
    }
    return display;
  }, []);

  const getSecondaryInfo = useCallback((quote: QuoteResponse) => {
    const parts = [];
    if (quote.status) {
      parts.push(quote.status.toLowerCase().replace("_", " "));
    }
    if (quote.total) {
      parts.push(formatConvertedAmount(quote.total, quote));
    }
    return parts.join(" • ");
  }, []);

  return (
    <EntitySelector<QuoteResponse>
      entities={quotes}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search quotes..."
      className={className}
      disabled={disabled}
      variant={variant}
      excludeId={excludeQuoteId}
      entityLabel="quotes"
      resultLimit={50}
    />
  );
}
